import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOrderStore } from "@/store/order";

type Props = {
    onSigned?: (dataUrl: string) => void;
    onCleared?: () => void;
};

export function InlineSignature({ onSigned, onCleared }: Props) {
    const { t } = useTranslation();
    const { setSignature, signatureUrl } = useOrderStore();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasDrawn, setHasDrawn] = useState(false);
    const lastPos = useRef<{ x: number; y: number } | null>(null);

    useEffect(() => {
        if (signatureUrl) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        // Back the canvas at device-pixel resolution so strokes stay crisp on
        // hi-DPI (retina) tablet/desktop screens instead of being upscaled.
        const dpr = window.devicePixelRatio || 1;
        canvas.width = canvas.offsetWidth * dpr;
        canvas.height = canvas.offsetHeight * dpr;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.strokeStyle = "#1a1a1a";
        ctx.lineWidth = 2 * dpr;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
    }, [signatureUrl]);

    function getPos(e: React.MouseEvent | React.TouchEvent) {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        // The backing-store size is fixed at mount; the CSS box can be wider
        // (desktop/tablet), so map client coords into canvas space by the
        // current scale instead of assuming 1:1.
        const scaleX = rect.width ? canvas.width / rect.width : 1;
        const scaleY = rect.height ? canvas.height / rect.height : 1;
        const client =
            "touches" in e
                ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
                : { x: e.clientX, y: e.clientY };
        return {
            x: (client.x - rect.left) * scaleX,
            y: (client.y - rect.top) * scaleY,
        };
    }

    function handleStart(e: React.MouseEvent | React.TouchEvent) {
        e.preventDefault();
        setIsDrawing(true);
        setHasDrawn(true);
        lastPos.current = getPos(e);
    }

    function handleMove(e: React.MouseEvent | React.TouchEvent) {
        e.preventDefault();
        if (!isDrawing || !lastPos.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (!canvas || !ctx) return;
        const pos = getPos(e);
        ctx.beginPath();
        ctx.moveTo(lastPos.current.x, lastPos.current.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
        lastPos.current = pos;
    }

    function handleEnd() {
        setIsDrawing(false);
        lastPos.current = null;
        const canvas = canvasRef.current;
        if (canvas && hasDrawn) {
            const dataUrl = canvas.toDataURL("image/png");
            setSignature(dataUrl);
            onSigned?.(dataUrl);
        }
    }

    function handleClear() {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (canvas && ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
        setHasDrawn(false);
        setSignature("");
        onCleared?.();
    }

    if (signatureUrl) {
        return (
            <div className="rounded-2xl border-2 border-primary bg-white overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-800">
                        {t("confirmStep.signature.title")}
                    </p>
                </div>
                <div
                    className="flex items-center justify-center bg-gray-50 p-4"
                    style={{ height: 180 }}
                >
                    <img
                        data-testid="signature-readonly"
                        src={signatureUrl}
                        alt={t("confirmStep.signature.title")}
                        className="max-h-full max-w-full object-contain"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-2xl border-2 border-primary bg-white overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                    <p className="text-sm font-semibold text-gray-800">
                        {t("confirmStep.signature.title")}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {t("confirmStep.signature.subtitle")}
                    </p>
                </div>
                {hasDrawn && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="text-xs text-gray-400 hover:text-gray-600 underline"
                    >
                        {t("confirmStep.signature.clear")}
                    </button>
                )}
            </div>

            <div className="relative bg-gray-50" style={{ height: 180 }}>
                <canvas
                    ref={canvasRef}
                    data-testid="signature-canvas"
                    className="absolute inset-0 w-full h-full cursor-crosshair touch-none"
                    onMouseDown={handleStart}
                    onMouseMove={handleMove}
                    onMouseUp={handleEnd}
                    onMouseLeave={handleEnd}
                    onTouchStart={handleStart}
                    onTouchMove={handleMove}
                    onTouchEnd={handleEnd}
                />
                {!hasDrawn && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-400 pointer-events-none">
                        <svg
                            className="size-8 text-primary"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={1.5}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                        </svg>
                        <span className="text-sm">
                            {t("confirmStep.signature.placeholder")}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}
